// T12 gallery extras inside <media-gallery>: caption pill ("Label · 1 / 8"), "See it in a room",
// mobile dot indicator for the native swipe slider, and a 2x hover lens on desktop.
if (!customElements.get('tac-gallery')) {
  customElements.define(
    'tac-gallery',
    class TacGallery extends HTMLElement {
      connectedCallback() {
        this.gallery = this.closest('media-gallery');
        this.viewer = this.closest('[id^="GalleryViewer"]');
        this.list = this.viewer?.querySelector('ul[id^="Slider-Gallery"]');
        if (!this.gallery || !this.list) return;
        this.caption = this.querySelector('.tac-gallery__caption');
        this.captionText = this.caption.querySelector('.tac-gallery__caption-text');
        this.dots = this.querySelector('.tac-gallery__dots');
        this.mobile = window.matchMedia('(max-width: 749px)');
        this.lensMql = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

        this.schedule = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };
        this.list.addEventListener('scroll', this.schedule, { passive: true });
        // Thumbnail clicks toggle .is-active; variant changes reorder/replace slides (product-info.js)
        this.observer = new MutationObserver(this.schedule);
        this.observer.observe(this.list, { childList: true, attributes: true, attributeFilter: ['class'], subtree: true });
        this.mobile.addEventListener('change', this.schedule);
        window.addEventListener('resize', this.schedule);

        this.querySelector('.tac-gallery__wall')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.gallery.setActiveMedia(this.dataset.room, false);
        });

        this.viewer.addEventListener('mousemove', this.onLens.bind(this));
        this.viewer.addEventListener('mouseleave', () => this.hideLens());
        this.update();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.list?.removeEventListener('scroll', this.schedule);
        this.mobile?.removeEventListener('change', this.schedule);
        window.removeEventListener('resize', this.schedule);
      }

      slides() {
        return Array.from(this.list.querySelectorAll(':scope > li[data-media-id]'));
      }

      // Fractional slide position: scroll-based on mobile, the .is-active slide on larger screens
      position(slides) {
        if (this.mobile.matches && slides.length > 1) {
          const step = slides[1].offsetLeft - slides[0].offsetLeft;
          return step > 0 ? this.list.scrollLeft / step : 0;
        }
        return Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      }

      update() {
        const slides = this.slides();
        if (!slides.length) return;
        const pos = Math.min(slides.length - 1, Math.max(0, this.position(slides)));
        const index = Math.round(pos);
        const label = slides[index].dataset.tacLabel;
        this.captionText.textContent = `${label ? `${label} · ` : ''}${index + 1} / ${slides.length}`;
        this.placeCaption(slides[index]);

        if (!this.dots) return;
        if (this.dots.children.length !== slides.length) {
          this.dots.replaceChildren(...slides.map(() => document.createElement('span')));
        }
        // Active dot stretches to a pill; mid-swipe the two neighbours share the weight
        Array.from(this.dots.children).forEach((dot, i) => {
          dot.style.setProperty('--w', Math.max(0, 1 - Math.abs(pos - i)).toFixed(3));
        });
      }

      // Pin the pill to the bottom-right of the picture box, which is narrower than the slide when height-constrained
      placeCaption(slide) {
        const media = slide.querySelector('.product-media-container');
        if (!media || this.mobile.matches) return;
        const box = media.getBoundingClientRect();
        const viewer = this.viewer.getBoundingClientRect();
        this.caption.style.right = `${viewer.right - box.right + 16}px`;
        this.caption.style.bottom = `${viewer.bottom - box.bottom + 16}px`;
      }

      onLens(event) {
        if (!this.lensMql.matches) return this.hideLens();
        const container = event.target.closest('.product-media-container.media-type-image');
        const img = container?.querySelector('.product__media img');
        if (!img || !img.naturalWidth) return this.hideLens();

        // Image is object-fit: contain, so find the drawn picture inside the img box
        const box = img.getBoundingClientRect();
        const ratio = img.naturalWidth / img.naturalHeight;
        let w = box.width;
        let h = box.height;
        if (w / h > ratio) w = h * ratio;
        else h = w / ratio;
        const left = box.left + (box.width - w) / 2;
        const top = box.top + (box.height - h) / 2;
        const x = event.clientX - left;
        const y = event.clientY - top;
        if (x < 0 || y < 0 || x > w || y > h) return this.hideLens();

        if (!this.lens || this.lens.parentElement !== container) {
          this.lens?.remove();
          this.lens = document.createElement('div');
          this.lens.className = 'tac-lens';
          this.lens.setAttribute('aria-hidden', 'true');
          container.append(this.lens);
        }
        const src = img.srcset ? img.srcset.split(',').pop().trim().split(' ')[0] : img.currentSrc;
        if (this.lens.dataset.src !== src) {
          this.lens.dataset.src = src;
          this.lens.style.backgroundImage = `url("${src}")`;
        }
        const r = this.lens.offsetWidth / 2 || 90;
        const cbox = container.getBoundingClientRect();
        this.lens.style.left = `${event.clientX - cbox.left - r}px`;
        this.lens.style.top = `${event.clientY - cbox.top - r}px`;
        this.lens.style.backgroundSize = `${w * 2}px ${h * 2}px`;
        this.lens.style.backgroundPosition = `${r - x * 2}px ${r - y * 2}px`;
        this.lens.hidden = false;
      }

      hideLens() {
        if (this.lens) this.lens.hidden = true;
      }
    }
  );
}
